/**
 * Les gestes de « Préparer mon live », ceux de l'ancien écran (`PrepareLiveScreen`) :
 *  - un élève en programme : « Choisir un chapitre » ouvre la boîte de ses programmes
 *    (`getStudyProgramsAsTutor`, le programme du chapitre déjà retenu ouvert d'office), un chapitre
 *    touché l'enregistre (`updateChapterOccurrence`) ;
 *  - un élève hors programme : sa matière s'enregistre au choix (`updateOccurrenceSubjects`) ;
 *  - chaque écriture fait tourner la ligne de l'élève, puis relit les lives ; un refus ferme la boîte
 *    et le dit (l'ancien toast d'erreur) ;
 *  - les événements `live_preparation.*` de l'ancien écran, ici et jamais dans le DS ;
 *  - sans stage à préparer (décision du 02/11), l'écran renvoie à « Ma journée ».
 * Les deux écritures sont mesurées (`live_preparation.chapter`, `live_preparation.subject`).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useReducer, useRef } from "react"
import { useSelector } from "react-redux"

import { liveService } from "../../../../../../data/services/liveService"
import {
    prepareUpdateKey,
    preparationCompletionEvents,
    preparationEventName,
    type ApiPrepareOccurrence,
    type TutorPrepareStudentRow,
} from "../../../../adapters/tutor/Lives/tutorPrepareLiveAdapter"
import {
    tutorPrepareLiveViewModel,
    tutorPrepareStageViewModel,
} from "../../../../view-models/tutor/Lives/tutorPrepareLiveViewModel"
import { type UITutorPrepareOccurrence, type UITutorPrepareStudent } from "../../../types/tutorLives"
import { type UITutorProgramChapter } from "../../../types/tutorEleves"
import { track } from "../../../hooks/trackEvent"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"
import { useTutorProgramPicker } from "./useTutorProgramPicker"

type StackNavigation = {
    canGoBack: () => boolean
    navigate: (route: string, params?: object) => void
    replace: (route: string, params?: object) => void
}

const SURFACE = "tutor_prepare_live"

/** Ce que dit le bandeau quand une mise à jour ne passe pas. */
export const PREPARE_UPDATE_FAILED = "La préparation n'a pas été enregistrée. Réessaie dans un instant."

/** L'élève dont la boîte des programmes est ouverte, la ligne qui tourne, le refus à dire : ils changent ensemble. */
type PrepareUi = {
    pickerStudent: TutorPrepareStudentRow | null
    updatingKey: string | null
    failure: string | null
}

type PrepareUiAction =
    | { type: "OPEN_PICKER"; student: TutorPrepareStudentRow }
    | { type: "CLOSE_PICKER" }
    | { type: "SENDING"; key: string }
    | { type: "WRITTEN" }
    | { type: "FAILED" }
    | { type: "DISMISS_FAILURE" }

const INITIAL: PrepareUi = { pickerStudent: null, updatingKey: null, failure: null }

function prepareUiReducer(state: PrepareUi, action: PrepareUiAction): PrepareUi {
    switch (action.type) {
        case "OPEN_PICKER":
            return { ...state, pickerStudent: action.student }
        case "CLOSE_PICKER":
            return { ...state, pickerStudent: null }
        case "SENDING":
            return { ...state, updatingKey: action.key }
        // Une écriture finie ferme la boîte et arrête la ligne ; un refus le dit.
        case "WRITTEN":
            return { ...state, pickerStudent: null, updatingKey: null }
        case "FAILED":
            return { ...state, pickerStudent: null, updatingKey: null, failure: PREPARE_UPDATE_FAILED }
        case "DISMISS_FAILURE":
            return { ...state, failure: null }
    }
}

/** La page rend les élèves qu'on lui a passés : ceux du view-model, qui portent leur occurrence. */
const asRow = (student: UITutorPrepareStudent) => student as TutorPrepareStudentRow

export function useTutorPrepareLiveState(raw: ApiPrepareOccurrence[] | null, refetch: () => void) {
    const navigation = useNavigation() as unknown as StackNavigation
    const [ui, dispatchUi] = useReducer(prepareUiReducer, INITIAL)
    const close = useBackOr("menu")

    // Sans stage à préparer : « Ma journée » (l'onglet `dashboard` de la coquille).
    const stage = useSelector(tutorPrepareStageViewModel)
    const noStage = stage.accountLoaded && !stage.hasStage
    useEffect(() => {
        if (!noStage) return
        if (navigation.canGoBack()) navigation.navigate("menu", { screen: "dashboard" })
        else navigation.replace("menu", { screen: "dashboard" })
    }, [noStage, navigation])

    // ── Les événements de complétude, une fois chacun, sur les lives que la page montre (les mêmes
    // arguments que l'écran : le view-model ne recalcule pas). ──
    const { occurrences } = useSelector((s: unknown) =>
        tutorPrepareLiveViewModel(s, { raw, updatingKey: ui.updatingKey })
    )
    const trackedRef = useRef(new Set<string>())
    useEffect(() => {
        if (raw == null) return
        for (const key of preparationCompletionEvents(occurrences, trackedRef.current)) {
            trackedRef.current.add(key)
            track({ name: preparationEventName(key) })
        }
    }, [raw, occurrences])

    // ── Les programmes de l'élève de la boîte ──
    const picker = useTutorProgramPicker(ui.pickerStudent?.chapter?.id)

    // ── Les écritures ──
    const afterWrite = useCallback(() => {
        dispatchUi({ type: "WRITTEN" })
        refetch()
    }, [refetch])
    const afterFailure = useCallback(() => dispatchUi({ type: "FAILED" }), [])
    const chapterWrite = useApiMutation({
        service: liveService.updateChapterOccurrence,
        gesture: "live_preparation.chapter",
        surface: SURFACE,
        skipWhilePending: true,
        onSuccess: afterWrite,
        onError: afterFailure,
    })
    const subjectWrite = useApiMutation({
        service: liveService.updateOccurrenceSubjects,
        gesture: "live_preparation.subject",
        surface: SURFACE,
        skipWhilePending: true,
        onSuccess: afterWrite,
        onError: afterFailure,
    })

    const { load: loadPrograms, cancel: cancelPrograms } = picker
    const onChooseChapter = useCallback(
        (_occurrence: UITutorPrepareOccurrence, student: UITutorPrepareStudent) => {
            chapterWrite.gesture.opened()
            dispatchUi({ type: "OPEN_PICKER", student: asRow(student) })
            loadPrograms(student.id)
        },
        [chapterWrite.gesture, loadPrograms]
    )

    const pickerStudent = ui.pickerStudent
    const onSelectChapter = useCallback(
        (chapter: UITutorProgramChapter) => {
            if (pickerStudent == null) return
            track({ name: "live_preparation.student_prepared" })
            const sent = chapterWrite.start(
                { id: pickerStudent.occurrenceId },
                { student_id: pickerStudent.id, chapter_id: chapter.id }
            )
            if (sent)
                dispatchUi({ type: "SENDING", key: prepareUpdateKey(pickerStudent.id, pickerStudent.occurrenceId) })
        },
        [pickerStudent, chapterWrite]
    )

    const onSelectSubject = useCallback(
        (_occurrence: UITutorPrepareOccurrence, student: UITutorPrepareStudent, subjectId: string | number) => {
            const row = asRow(student)
            track({ name: "live_preparation.student_prepared" })
            const sent = subjectWrite.start({ id: row.occurrenceId }, { student_id: row.id, subject_ids: [subjectId] })
            if (sent) dispatchUi({ type: "SENDING", key: prepareUpdateKey(row.id, row.occurrenceId) })
        },
        [subjectWrite]
    )

    const chapterPicker =
        pickerStudent == null
            ? null
            : {
                  student: pickerStudent,
                  programs: picker.programs,
                  selectedProgramId: picker.selectedProgramId,
                  loading: picker.loading,
                  error: picker.error,
                  onRetry: picker.onRetry,
              }

    return {
        updatingKey: ui.updatingKey,
        /** Sans stage, l'écran s'en va : il ne montre rien entre-temps. */
        leaving: noStage,
        gestures: {
            onClose: close,
            onChooseChapter,
            onSelectSubject,
            chapterPicker,
            onSelectProgram: picker.onSelectProgram,
            onSelectChapter,
            onCloseChapterPicker: () => {
                cancelPrograms()
                dispatchUi({ type: "CLOSE_PICKER" })
            },
        },
        failure: ui.failure,
        dismissFailure: () => dispatchUi({ type: "DISMISS_FAILURE" }),
    }
}
